import { useCallback, useEffect } from 'react';
import { OnMessageHandler, PostMessage, SendMessage } from '~/shared/types';
import isValidMessage from '~/shared/utils/isValidMessage';
import sharedSendMessage from '~/shared/utils/sendMessage';
import log from '~/shared/utils/log';

/**
 * Allows to send messages to the parent window (bootstrap) and listen to messages from the parent window.
 */
const useMessaging = (onMessage?: OnMessageHandler): SendMessage => {
  const sendMessage = useCallback(
    (message: PostMessage) => {
      sharedSendMessage(message, window.parent);
    },
    [window.parent, sharedSendMessage],
  );

  const messageHandler = useCallback(
    (event: MessageEvent) => {
      try {
        // We only receive stringified messages, ignore any message that isn't stringified
        if (typeof event.data !== 'string') return;
        const parsedMessage: PostMessage = JSON.parse(event.data);
        log('Received message - widget', parsedMessage);
        // Also not a correct message, ignore it
        if (!isValidMessage(parsedMessage)) return;

        if (onMessage) {
          onMessage(parsedMessage);
        }
      } catch {
        if (
          typeof event.data === 'string' &&
          !event.data.includes('[iFrameSizer')
        ) {
          // eslint-disable-next-line no-console
          console.warn(`Unable to parse message \`${event.data}\``);
        }
      }
    },

    [onMessage],
  );

  useEffect(() => {
    window.addEventListener('message', messageHandler);

    return () => {
      window.removeEventListener('message', messageHandler);
    };
  }, [messageHandler]);

  return sendMessage;
};

export default useMessaging;
